import React, { useEffect, useMemo, useRef, useState } from "react";

export interface ChartSeries {
    name: string;
    color: string;
    values: (number | null)[];
}

interface Props {
    dates: Date[];
    series: ChartSeries[];
    yLabel?: string;
    unit?: string;
    height?: number;
    zeroLine?: boolean;
    yMin?: number;
    yMax?: number;
}

const MARGIN = { top: 10, right: 12, bottom: 24, left: 44 };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const niceStep = (range: number, targetTicks = 4) => {
    const raw = range / targetTicks;
    const pow = Math.pow(10, Math.floor(Math.log10(raw)));
    const n = raw / pow;
    return (n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10) * pow;
};

const StationLineChart: React.FC<Props> = ({ dates, series, yLabel, unit = "", height = 200, zeroLine, yMin, yMax }) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(600);
    const [hoverIdx, setHoverIdx] = useState<number | null>(null);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const { lo, hi, ticks } = useMemo(() => {
        const all = series.flatMap(s => s.values).filter((v): v is number => v != null);
        let lo = yMin ?? Math.min(...all, zeroLine ? 0 : Infinity);
        let hi = yMax ?? Math.max(...all, zeroLine ? 0 : -Infinity);
        if (lo === hi) { lo -= 1; hi += 1; }
        const step = niceStep(hi - lo);
        lo = Math.floor(lo / step) * step;
        hi = Math.ceil(hi / step) * step;
        const ticks: number[] = [];
        for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Math.round(v * 100) / 100);
        return { lo, hi, ticks };
    }, [series, yMin, yMax, zeroLine]);

    const innerW = Math.max(10, width - MARGIN.left - MARGIN.right);
    const innerH = height - MARGIN.top - MARGIN.bottom;
    const x = (i: number) => MARGIN.left + (i / Math.max(1, dates.length - 1)) * innerW;
    const y = (v: number) => MARGIN.top + (1 - (v - lo) / (hi - lo)) * innerH;

    const pathFor = (values: (number | null)[]) => {
        let d = "";
        let pen = false;
        values.forEach((v, i) => {
            if (v == null) { pen = false; return; }
            d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
            pen = true;
        });
        return d;
    };

    const monthTicks = dates
        .map((d, i) => ({ d, i }))
        .filter(({ d }) => d.getUTCDate() === 1);

    const onMove = (e: React.MouseEvent<SVGRectElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const frac = (e.clientX - rect.left) / rect.width;
        setHoverIdx(Math.min(dates.length - 1, Math.max(0, Math.round(frac * (dates.length - 1)))));
    };

    const hoverDate = hoverIdx != null ? dates[hoverIdx] : null;
    const tooltipLeft = hoverIdx != null ? x(hoverIdx) : 0;

    return (
        <div className="st-chart">
            <div className="st-chart-legend">
                {series.map(s => (
                    <span key={s.name} className="st-chart-legend-item">
                        <span className="st-chart-swatch" style={{ background: s.color }} />
                        {s.name}
                    </span>
                ))}
            </div>
            <div ref={wrapRef} className="st-chart-canvas" style={{ height }}>
                <svg width={width} height={height} role="img" aria-label={yLabel ?? "Time series chart"}>
                    {ticks.map(t => (
                        <g key={t}>
                            <line x1={MARGIN.left} x2={width - MARGIN.right} y1={y(t)} y2={y(t)} className="st-chart-grid" />
                            <text x={MARGIN.left - 6} y={y(t)} className="st-chart-tick" textAnchor="end" dominantBaseline="middle">{t}</text>
                        </g>
                    ))}
                    {monthTicks.map(({ d, i }) => (
                        <g key={i}>
                            <line x1={x(i)} x2={x(i)} y1={MARGIN.top} y2={height - MARGIN.bottom} className="st-chart-grid" />
                            <text x={x(i)} y={height - 6} className="st-chart-tick" textAnchor="middle">{MONTHS[d.getUTCMonth()]}</text>
                        </g>
                    ))}
                    {zeroLine && lo < 0 && hi > 0 && (
                        <line x1={MARGIN.left} x2={width - MARGIN.right} y1={y(0)} y2={y(0)} className="st-chart-zero" />
                    )}
                    {yLabel && (
                        <text transform={`translate(11, ${MARGIN.top + innerH / 2}) rotate(-90)`} className="st-chart-ylabel" textAnchor="middle">
                            {yLabel}
                        </text>
                    )}
                    {series.map(s => (
                        <path key={s.name} d={pathFor(s.values)} fill="none" stroke={s.color} strokeWidth={1.4} strokeLinejoin="round" />
                    ))}
                    {hoverIdx != null && (
                        <line x1={x(hoverIdx)} x2={x(hoverIdx)} y1={MARGIN.top} y2={height - MARGIN.bottom} className="st-chart-hover" />
                    )}
                    <rect
                        x={MARGIN.left}
                        y={MARGIN.top}
                        width={innerW}
                        height={innerH}
                        fill="transparent"
                        onMouseMove={onMove}
                        onMouseLeave={() => setHoverIdx(null)}
                    />
                </svg>
                {hoverDate && (
                    <div
                        className="st-chart-tooltip"
                        style={{
                            left: tooltipLeft,
                            transform: tooltipLeft > width * 0.7 ? "translateX(calc(-100% - 10px))" : "translateX(10px)",
                        }}
                    >
                        <div className="st-chart-tooltip-date">
                            {MONTHS[hoverDate.getUTCMonth()]} {hoverDate.getUTCDate()}, {hoverDate.getUTCFullYear()}
                        </div>
                        {series.map(s => {
                            const v = s.values[hoverIdx!];
                            return (
                                <div key={s.name} className="st-chart-tooltip-row">
                                    <span className="st-chart-swatch" style={{ background: s.color }} />
                                    {s.name}: <strong>{v == null ? "missing" : `${v}${unit}`}</strong>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StationLineChart;
